import { Button } from "@/components/ui/button";
import LocaleSwitcher from "@/components/ui/locale-switcher";
import ReservedAreaButton from "@/components/layout/reserved-area-button";
import BrandLogo from "@/components/layout/brand-logo";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import ThemeToggle from "@/components/ui/theme-toggle";
import { useDeferredPublicEnhancement } from "@/hooks/use-deferred-public-enhancement";
import { usePublicServices } from "@/pages/services/_lib/use-public-services";
import {softwareNavLabel,softwarePath} from "../../../shared/software-copy";
import { api } from "@/convex/_generated/api";
import { serviceIcon } from "@/lib/service-visuals";
import { inquiryPath, localizedPath, type RouteKey } from "@/lib/routes";
import {
  DEFAULT_PUBLIC_MENU_ORDER,
  readInitialPublicNavigation,
  resolvePublicNavigation,
  type PublicMenuKey,
} from "@/lib/public-navigation";
import {
  readInitialPublicBranding,
  resolvePublicBranding,
  sameBrandAsset,
} from "@/lib/public-branding";
import { cn } from "@/lib/utils";
import {
  BriefcaseBusiness,
  Building2,
  CircleHelp,
  ChevronDown,
  FileText,
  Handshake,
  LifeBuoy,
  Menu,
  MessageSquareQuote,
  Newspaper,
  ShoppingCart,
} from "lucide-react";
import { useQuery } from "convex/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";
import { SECRETARIAT_COPY, SECRETARIAT_SLUGS } from "@/lib/secretariat";
import { isSupportedLocale } from "@/i18n-locales";

// Top-level nav items (the "Gestione Contenuti" voice is intentionally NOT here:
// it now lives inside the Services dropdown to keep the bar clean). Each item has
// its own gradient so it lights up with a distinct colour on hover/tap.
const NAV_ITEMS: {
  key: RouteKey;
  menuKey: "portfolio" | "tools";
  label: string;
  gradient: string;
}[] = [
  {
    key: "portfolio",
    menuKey: "portfolio",
    label: "nav.portfolio",
    gradient: "from-violet-500 to-fuchsia-500",
  },
  {
    key: "tools",
    menuKey: "tools",
    label: "nav.tools",
    gradient: "from-blue-500 to-cyan-500",
  },
];

const SERVICES_GRADIENT = "from-indigo-500 to-blue-500";
const COMPANY_GRADIENT = "from-sky-500 to-blue-500";
const ECOSYSTEM_GRADIENT = "from-cyan-500 to-teal-500";
const ASSISTANCE_GRADIENT = "from-rose-500 to-pink-500";
const MAX_SAFE_HEADER_LOGO_WIDTH = 200;
const SHOP_URL = "https://shop.enkey.eu";

const COMPANY_ITEMS = [
  { key: "about" as const, label: "nav.about", icon: Building2 },
  {
    key: "careers" as const,
    label: "nav.careers",
    icon: BriefcaseBusiness,
  },
];

const ECOSYSTEM_ITEMS = [
  { key: "partners" as const, label: "nav.partners", icon: Handshake },
  { key: "blog" as const, label: "nav.blog", icon: Newspaper },
  { key: "shop" as const, label: "nav.shop", icon: ShoppingCart },
];

const ASSISTANCE_ITEMS = [
  { key: "contact" as const, label: "nav.support", icon: LifeBuoy },
  { key: "faq" as const, label: "nav.faq", icon: CircleHelp },
  {
    key: "inquiries" as const,
    label: "nav.inquiries",
    icon: MessageSquareQuote,
  },
];

// A coloured parallelogram nav item. The surface fills with its gradient and a
// glass-like light beam glides across on hover/tap. Works in light and dark mode.
//
// The active state renders a SOLID gradient background (a plain span, not a
// motion variant) so the current page is always clearly highlighted, regardless
// of hover/tap animation state. Only the non-active items animate their fill on
// hover, so the active background can never be washed out to white-on-light.
function ParallelogramItem({
  active,
  gradient,
  children,
}: {
  active?: boolean;
  gradient: string;
  children: React.ReactNode;
}) {
  return (
    <span
      data-nav-state={active ? "active" : "inactive"}
      className={cn(
        "enkey-nav-item group/para relative inline-flex -skew-x-12 items-center overflow-hidden rounded-[6px] border px-3 py-2 text-xs font-medium transition-colors duration-300 2xl:px-4 2xl:text-sm",
        active
          ? "border-transparent text-white dark:text-white"
          : "border-border/60 text-muted-foreground hover:text-white dark:hover:text-white",
      )}
    >
      {active ? (
        // Solid, always-on gradient background for the current page.
        <span
          className={cn(
            "pointer-events-none absolute inset-0 seasonal-brand-gradient bg-gradient-to-r",
            gradient,
          )}
        />
      ) : (
        // Coloured fill that fades in on hover only.
        <span
          className={cn(
            "pointer-events-none absolute inset-0 seasonal-brand-gradient bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover/para:opacity-100 group-focus-within/para:opacity-100",
            gradient,
          )}
        />
      )}
      {/* Glass light beam */}
      <span className="enkey-nav-sweep pointer-events-none absolute inset-y-0 w-[36%] bg-gradient-to-r from-transparent via-white/65 to-transparent" />
      <span className="relative z-10 inline-flex skew-x-12 items-center gap-1">
        {children}
      </span>
    </span>
  );
}

export default function SiteHeader() {
  const { t, i18n } = useTranslation("common");
  const { t: ts } = useTranslation("services");
  const { lng } = useParams<{ lng: string }>();
  const location = useLocation();
  const locale = lng ?? (["it","en","es","fr","de"].includes(i18n.language) ? i18n.language : "it");
  // Navigation labels are editorial CRM data and must be authoritative from
  // the first interactive render. Deferring this query would keep a renamed
  // service absent (or previously show its obsolete built-in label) until the
  // first pointer interaction.
  const { services } = usePublicServices(locale, ts);
  const secretariatLocale = isSupportedLocale(locale) ? locale : "it";
  const navigationServices = [
    ...services,
    {
      serviceKey: "secretariat",
      slug: SECRETARIAT_SLUGS[secretariatLocale],
      title: SECRETARIAT_COPY[secretariatLocale].title,
      tagline: "Valentina · Enkey Agency",
      icon: "Users",
      gradient: "from-blue-600 to-cyan-500",
    },
  ];
  const enhancementsReady = useDeferredPublicEnhancement();
  const initialBranding = useMemo(() => readInitialPublicBranding(), []);
  const liveBranding = useQuery(
    api.crm.branding.getPublic,
    enhancementsReady && !initialBranding ? {} : "skip",
  );
  const branding = resolvePublicBranding(initialBranding, liveBranding);
  const usesFallbackLogo = sameBrandAsset(
    branding?.logoUrl ?? "/brand/enkey-logo.png",
    "/brand/enkey-logo.png",
  );
  const usesWideMarkOnSquareCanvas =
    usesFallbackLogo ||
    (branding?.logoIntrinsicWidth === 1000 &&
      branding.logoIntrinsicHeight === 1000);
  const desktopHeaderLogoWidth = Math.min(
    MAX_SAFE_HEADER_LOGO_WIDTH,
    Math.max(80, branding?.headerLogoWidth ?? MAX_SAFE_HEADER_LOGO_WIDTH),
  );
  const initialNavigation = useMemo(() => readInitialPublicNavigation(), []);
  const navigation = useQuery(
    api.crm.navigation.getPublic,
    enhancementsReady && !initialNavigation ? {} : "skip",
  );
  const resolvedNavigation = resolvePublicNavigation(
    initialNavigation,
    navigation,
  );
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [assistanceOpen, setAssistanceOpen] = useState(false);
  const [ecosystemOpen, setEcosystemOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const [mobileAssistanceOpen, setMobileAssistanceOpen] = useState(false);
  const [mobileEcosystemOpen, setMobileEcosystemOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assistanceCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const ecosystemCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useLayoutEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > 8);
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setCompanyOpen(false);
    setAssistanceOpen(false);
    setEcosystemOpen(false);
    setMobileServicesOpen(false);
    setMobileCompanyOpen(false);
    setMobileAssistanceOpen(false);
    setMobileEcosystemOpen(false);
  }, [location.pathname]);

  const servicesPath = localizedPath("services", locale);
  const editorialPath = localizedPath("editorial", locale);
  const sponsoredPath = localizedPath("sponsored", locale);

  const isActive = (key: RouteKey) =>
    location.pathname === localizedPath(key, locale);
  const isServicesActive =
    location.pathname === servicesPath ||
    location.pathname.startsWith(`${servicesPath}/`) ||
    location.pathname === editorialPath ||
    location.pathname === sponsoredPath;
  const isCompanyActive = COMPANY_ITEMS.some(({ key }) => isActive(key));
  const isAssistanceActive = ASSISTANCE_ITEMS.some(({ key }) => isActive(key));
  const isEcosystemActive = ECOSYSTEM_ITEMS.some(({ key }) => isActive(key));
  const menuRank = (key: PublicMenuKey) => {
    const index = resolvedNavigation.items.indexOf(key);
    return (index < 0 ? DEFAULT_PUBLIC_MENU_ORDER.indexOf(key) : index) * 10;
  };

  // Hover intent: open immediately, close with a small delay so moving into the
  // panel doesn't dismiss it.
  const openServices = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const scheduleCloseServices = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesOpen(false), 140);
  };

  return (
    <header
      data-print-hidden
      data-header-scrolled={scrolled}
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled
          ? "border-b border-border bg-background/80 backdrop-blur-lg"
          : "bg-transparent",
      )}
    >
      <div
        data-header-scrolled={scrolled}
        className={cn(
          "enkey-site-header-grid mx-auto grid max-w-7xl items-center px-4 transition-all duration-300 sm:grid-cols-[minmax(0,1fr)_max-content] sm:grid-rows-1 sm:gap-x-3 sm:px-6 lg:px-8",
          scrolled
            ? "h-16 grid-cols-[minmax(0,1fr)_max-content] grid-rows-1 gap-x-1 py-2 sm:h-20 sm:py-0"
            : "min-h-36 grid-cols-1 grid-rows-[auto_auto] gap-y-2 py-3 sm:h-32 sm:min-h-0 sm:gap-y-0 sm:py-0",
        )}
      >
        <Link
          to={localizedPath("home", locale)}
          className={cn(
            "enkey-header-home flex w-full min-w-0 max-w-full items-center sm:col-start-1 sm:row-start-1 sm:justify-self-start sm:justify-start",
            scrolled
              ? "col-start-1 row-start-1 justify-start"
              : "col-span-full row-start-1 justify-center sm:col-span-1",
          )}
          aria-label={`${t("brand")} — ${t("nav.home")}`}
        >
          <span
            data-mobile-logo-frame
            className={cn(
              "flex shrink-0 items-center justify-center transition-all duration-300 sm:hidden",
              usesWideMarkOnSquareCanvas &&
                "overflow-hidden [&_picture]:block [&_picture]:h-full [&_picture]:w-full",
              scrolled ? "h-10 w-24" : "h-[5.5rem] w-52",
            )}
          >
            <BrandLogo
              eager
              src={branding?.logoUrl}
              intrinsicWidth={branding?.logoIntrinsicWidth}
              intrinsicHeight={branding?.logoIntrinsicHeight}
              sizes="(max-width: 639px) 208px, 1px"
              className={cn(
                "block h-full w-full transition-all duration-300",
                usesWideMarkOnSquareCanvas
                  ? "max-w-none object-cover"
                  : "object-contain",
              )}
            />
          </span>
          <BrandLogo
            eager
            src={branding?.logoUrl}
            displayWidth={desktopHeaderLogoWidth}
            intrinsicWidth={branding?.logoIntrinsicWidth}
            intrinsicHeight={branding?.logoIntrinsicHeight}
            sizes="(max-width: 1359px) 192px, 200px"
            className={cn(
              "enkey-header-logo-desktop hidden h-auto w-auto object-contain transition-all duration-300 sm:block",
              scrolled ? "max-h-12" : "max-h-24",
            )}
          />
        </Link>

        <nav
          className="enkey-header-nav hidden max-w-full items-center justify-center gap-1 whitespace-nowrap min-[1360px]:flex min-[1360px]:justify-self-center 2xl:gap-1.5"
          aria-label={t("nav.menu")}
        >
          <div
            className="relative"
            style={{ order: menuRank("company") }}
            onMouseEnter={() => {
              if (companyCloseTimer.current)
                clearTimeout(companyCloseTimer.current);
              setCompanyOpen(true);
            }}
            onMouseLeave={() => {
              companyCloseTimer.current = setTimeout(
                () => setCompanyOpen(false),
                140,
              );
            }}
          >
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={companyOpen}
              aria-controls="desktop-company-menu"
              onClick={() => setCompanyOpen((value) => !value)}
              className="cursor-pointer"
            >
              <ParallelogramItem
                active={isCompanyActive}
                gradient={COMPANY_GRADIENT}
              >
                {t("nav.company")}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-300",
                    companyOpen && "rotate-180",
                  )}
                />
              </ParallelogramItem>
            </button>
            <div
              id="desktop-company-menu"
              aria-hidden={!companyOpen}
              inert={!companyOpen}
              className={cn(
                "absolute left-0 top-full z-50 w-64 pt-3 transition-all duration-200",
                companyOpen
                  ? "pointer-events-auto translate-y-0 opacity-100"
                  : "pointer-events-none -translate-y-1 opacity-0",
              )}
            >
              <div className="rounded-2xl border border-border bg-popover/95 p-2 shadow-xl backdrop-blur-lg">
                {COMPANY_ITEMS.map(({ key, label, icon: Icon }) => (
                  <Link
                    key={key}
                    to={localizedPath(key, locale)}
                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition-colors hover:bg-accent"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-[18px] w-[18px]" />
                    </span>
                    {t(label)}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Services dropdown */}
          <div
            className="relative"
            style={{ order: menuRank("services") }}
            onMouseEnter={openServices}
            onMouseLeave={scheduleCloseServices}
          >
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={servicesOpen}
              aria-controls="desktop-services-menu"
              onClick={openServices}
              className="cursor-pointer"
            >
              <ParallelogramItem
                active={isServicesActive}
                gradient={SERVICES_GRADIENT}
              >
                {t("nav.services")}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-300",
                    servicesOpen && "rotate-180",
                  )}
                />
              </ParallelogramItem>
            </button>

            {/* Dropdown panel: anchored to the left edge of the trigger and clamped
                to the viewport so the left-hand items never get clipped on small screens. */}
            <div
              id="desktop-services-menu"
              aria-hidden={!servicesOpen}
              inert={!servicesOpen}
              className={cn(
                "absolute left-0 top-full z-50 w-[min(42rem,calc(100vw-2rem))] pt-3 transition-all duration-200",
                servicesOpen
                  ? "pointer-events-auto translate-y-0 opacity-100"
                  : "pointer-events-none -translate-y-1 opacity-0",
              )}
            >
              <div className="max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain rounded-2xl border border-border bg-popover/95 p-3 shadow-xl backdrop-blur-lg">
                <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-1">
                  {navigationServices.map((service) => {
                    const Icon = serviceIcon(service.icon);
                    return (
                      <Link
                        key={service.serviceKey}
                        to={`${servicesPath}/${service.slug}`}
                        className="group/it flex min-w-0 items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-accent"
                      >
                        <span
                          className={cn(
                            "mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg seasonal-brand-gradient bg-gradient-to-br text-white shadow-sm",
                            service.gradient,
                          )}
                        >
                          <Icon className="h-[18px] w-[18px]" />
                        </span>
                        <span className="min-w-0">
                          <span className="block break-words text-sm font-semibold leading-5 text-foreground">
                            {service.title}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {service.tagline}
                          </span>
                        </span>
                      </Link>
                    );
                  })}

                  <Link
                    to={editorialPath}
                    className="group/it flex min-w-0 items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-accent"
                  >
                    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg seasonal-brand-gradient bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-sm">
                      <FileText className="h-[18px] w-[18px]" />
                    </span>
                    <span className="min-w-0">
                      <span className="block break-words text-sm font-semibold leading-5 text-foreground">
                        {t("nav.editorial")}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {ts("editorial_card.tagline")}
                      </span>
                    </span>
                  </Link>

                  <Link
                    to={sponsoredPath}
                    className="group/it flex min-w-0 items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-accent"
                  >
                    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg seasonal-brand-gradient bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-sm">
                      <Newspaper className="h-[18px] w-[18px]" />
                    </span>
                    <span className="min-w-0">
                      <span className="block break-words text-sm font-semibold leading-5 text-foreground">
                        {t("nav.sponsored")}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {ts("content.tagline")}
                      </span>
                    </span>
                  </Link>
                </div>

                <div className="mt-2 border-t border-border pt-2">
                  <Link
                    to={servicesPath}
                    className="flex items-center justify-center rounded-xl bg-muted/60 px-3 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
                  >
                    {t("nav.all_services")}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <a href={softwarePath(locale)} className="cursor-pointer" style={{order:menuRank("tools")-1}}><ParallelogramItem active={/\/(software|logiciels)(\/|$)/.test(location.pathname)} gradient={SERVICES_GRADIENT}>{softwareNavLabel[locale]??"Software"}</ParallelogramItem></a>
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              to={localizedPath(item.key, locale)}
              className="cursor-pointer"
              style={{ order: menuRank(item.menuKey) }}
            >
              <ParallelogramItem
                active={isActive(item.key)}
                gradient={item.gradient}
              >
                {t(item.label)}
              </ParallelogramItem>
            </Link>
          ))}

          <div
            className="relative"
            style={{ order: menuRank("support") }}
            onMouseEnter={() => {
              if (assistanceCloseTimer.current)
                clearTimeout(assistanceCloseTimer.current);
              setAssistanceOpen(true);
            }}
            onMouseLeave={() => {
              assistanceCloseTimer.current = setTimeout(
                () => setAssistanceOpen(false),
                140,
              );
            }}
          >
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={assistanceOpen}
              aria-controls="desktop-assistance-menu"
              onClick={() => setAssistanceOpen((value) => !value)}
              className="cursor-pointer"
            >
              <ParallelogramItem
                active={isAssistanceActive}
                gradient={ASSISTANCE_GRADIENT}
              >
                {t("nav.assistance")}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-300",
                    assistanceOpen && "rotate-180",
                  )}
                />
              </ParallelogramItem>
            </button>
            <div
              id="desktop-assistance-menu"
              aria-hidden={!assistanceOpen}
              inert={!assistanceOpen}
              className={cn(
                "absolute right-0 top-full z-50 w-72 pt-3 transition-all duration-200",
                assistanceOpen
                  ? "pointer-events-auto translate-y-0 opacity-100"
                  : "pointer-events-none -translate-y-1 opacity-0",
              )}
            >
              <div className="rounded-2xl border border-border bg-popover/95 p-2 shadow-xl backdrop-blur-lg">
                {ASSISTANCE_ITEMS.map(({ key, label, icon: Icon }) => (
                  <Link
                    key={key}
                    to={localizedPath(key, locale)}
                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition-colors hover:bg-accent"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600">
                      <Icon className="h-[18px] w-[18px]" />
                    </span>
                    {t(label)}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div
            className="relative"
            style={{ order: menuRank("network") }}
            onMouseEnter={() => {
              if (ecosystemCloseTimer.current)
                clearTimeout(ecosystemCloseTimer.current);
              setEcosystemOpen(true);
            }}
            onMouseLeave={() => {
              ecosystemCloseTimer.current = setTimeout(
                () => setEcosystemOpen(false),
                140,
              );
            }}
          >
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={ecosystemOpen}
              aria-controls="desktop-ecosystem-menu"
              onClick={() => setEcosystemOpen((value) => !value)}
              className="cursor-pointer"
            >
              <ParallelogramItem
                active={isEcosystemActive}
                gradient={ECOSYSTEM_GRADIENT}
              >
                {t("nav.ecosystem")}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-300",
                    ecosystemOpen && "rotate-180",
                  )}
                />
              </ParallelogramItem>
            </button>
            <div
              id="desktop-ecosystem-menu"
              aria-hidden={!ecosystemOpen}
              inert={!ecosystemOpen}
              className={cn(
                "absolute right-0 top-full z-50 w-64 pt-3 transition-all duration-200",
                ecosystemOpen
                  ? "pointer-events-auto translate-y-0 opacity-100"
                  : "pointer-events-none -translate-y-1 opacity-0",
              )}
            >
              <div className="rounded-2xl border border-border bg-popover/95 p-2 shadow-xl backdrop-blur-lg">
                {ECOSYSTEM_ITEMS.map(({ key, label, icon: Icon }) => (
                  <Link
                    key={key}
                    to={localizedPath(key, locale)}
                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition-colors hover:bg-accent"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-[18px] w-[18px]" />
                    </span>
                    {t(label)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div
          data-header-actions
          className={cn(
            "enkey-header-actions relative z-10 flex shrink-0 items-center sm:col-span-1 sm:col-start-2 sm:row-start-1 sm:w-auto sm:min-w-max sm:justify-self-end sm:gap-1",
            scrolled
              ? "col-start-2 row-start-1 w-auto min-w-max justify-end gap-0"
              : "col-span-full row-start-2 w-full min-w-0 justify-center gap-1",
          )}
        >
          <LocaleSwitcher />
          <ThemeToggle />
          <Button
            asChild
            variant="ghost"
            size="icon"
            data-header-shop-link
            className="shrink-0 border border-emerald-500/35 bg-emerald-500/12 text-emerald-700 shadow-sm hover:border-emerald-600 hover:bg-emerald-600 hover:text-white dark:border-emerald-400/45 dark:bg-emerald-400/15 dark:text-emerald-300 dark:hover:bg-emerald-500 dark:hover:text-white"
          >
            <a
              href={SHOP_URL}
              aria-label={t("nav.shop")}
              title={t("nav.shop")}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
            >
              <ShoppingCart className="h-5 w-5" aria-hidden="true" />
            </a>
          </Button>
          <ReservedAreaButton className="ml-1" compact />
          {!scrolled && (
            <Button
              asChild
              data-mobile-header-cta
              className="mx-1 h-10 min-w-0 flex-1 px-2 text-xs sm:hidden"
            >
              <Link
                to={inquiryPath(locale, { requestType: "new_project" })}
                aria-label={t("cta.start_project")}
              >
                <span className="truncate">{t("cta.start_project")}</span>
              </Link>
            </Button>
          )}
          <Button
            asChild
            data-desktop-header-cta
            className="enkey-desktop-header-cta ml-1 h-10 px-4 text-sm"
          >
            <Link
              to={inquiryPath(locale, { requestType: "new_project" })}
              aria-label={t("cta.start_project")}
            >
              <span className="truncate">{t("cta.start_project")}</span>
            </Link>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <Button
              variant="ghost"
              size="icon"
              className="min-[1360px]:hidden"
              type="button"
              onClick={() => setOpen(true)}
              aria-label={t("nav.menu")}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={open ? "mobile-site-menu" : undefined}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <SheetContent
              id="mobile-site-menu"
              side="right"
              className="w-80 overflow-y-auto"
            >
              <SheetHeader>
                <SheetTitle className="font-display">
                  {t("nav.menu")}
                </SheetTitle>
              </SheetHeader>
              <nav
                className="mt-2 flex flex-col gap-1 px-2 pb-6"
                aria-label={t("nav.menu")}
              >
                <button
                  type="button"
                  style={{ order: menuRank("company") }}
                  onClick={() => setMobileCompanyOpen((value) => !value)}
                  aria-expanded={mobileCompanyOpen}
                  aria-controls="mobile-company-menu"
                  className={cn(
                    "flex cursor-pointer items-center justify-between rounded-lg px-3 py-3 text-base font-medium transition-colors",
                    isCompanyActive
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted",
                  )}
                >
                  {t("nav.company")}
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-300",
                      mobileCompanyOpen && "rotate-180",
                    )}
                  />
                </button>
                {mobileCompanyOpen && (
                  <div
                    id="mobile-company-menu"
                    className="mb-1 flex flex-col gap-0.5 pl-3"
                    style={{ order: menuRank("company") }}
                  >
                    {COMPANY_ITEMS.map(({ key, label, icon: Icon }) => (
                      <Link
                        key={key}
                        to={localizedPath(key, locale)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted"
                      >
                        <Icon className="h-4 w-4 text-primary" />
                        {t(label)}
                      </Link>
                    ))}
                  </div>
                )}

                {/* Services: expandable section */}
                <button
                  type="button"
                  style={{ order: menuRank("services") }}
                  onClick={() => setMobileServicesOpen((v) => !v)}
                  aria-expanded={mobileServicesOpen}
                  aria-controls="mobile-services-menu"
                  className={cn(
                    "flex cursor-pointer items-center justify-between rounded-lg px-3 py-3 text-base font-medium transition-colors",
                    isServicesActive
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted",
                  )}
                >
                  {t("nav.services")}
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-300",
                      mobileServicesOpen && "rotate-180",
                    )}
                  />
                </button>
                {mobileServicesOpen && (
                  <div
                    id="mobile-services-menu"
                    className="mb-1 flex flex-col gap-0.5 pl-3"
                    style={{ order: menuRank("services") }}
                  >
                    {navigationServices.map((service) => {
                      const Icon = serviceIcon(service.icon);
                      return (
                        <Link
                          key={service.serviceKey}
                          to={`${servicesPath}/${service.slug}`}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted"
                        >
                          <span
                            className={cn(
                              "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md seasonal-brand-gradient bg-gradient-to-br text-white",
                              service.gradient,
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          {service.title}
                        </Link>
                      );
                    })}
                    <Link
                      to={editorialPath}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted"
                    >
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md seasonal-brand-gradient bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                        <FileText className="h-4 w-4" />
                      </span>
                      {t("nav.editorial")}
                    </Link>
                    <Link
                      to={sponsoredPath}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted"
                    >
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md seasonal-brand-gradient bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                        <Newspaper className="h-4 w-4" />
                      </span>
                      {t("nav.sponsored")}
                    </Link>
                    <Link
                      to={servicesPath}
                      className="rounded-lg px-3 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
                    >
                      {t("nav.all_services")}
                    </Link>
                  </div>
                )}

                <a href={softwarePath(locale)} style={{order:menuRank("tools")-1}} className="rounded-lg px-3 py-3 text-base font-medium hover:bg-muted">{softwareNavLabel[locale]??"Software"}</a>
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.key}
                    to={localizedPath(item.key, locale)}
                    style={{ order: menuRank(item.menuKey) }}
                    className={cn(
                      "rounded-lg px-3 py-3 text-base font-medium transition-colors",
                      isActive(item.key)
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted",
                    )}
                  >
                    {t(item.label)}
                  </Link>
                ))}
                <button
                  type="button"
                  style={{ order: menuRank("support") }}
                  onClick={() => setMobileAssistanceOpen((value) => !value)}
                  aria-expanded={mobileAssistanceOpen}
                  aria-controls="mobile-assistance-menu"
                  className={cn(
                    "flex cursor-pointer items-center justify-between rounded-lg px-3 py-3 text-base font-medium transition-colors",
                    isAssistanceActive
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted",
                  )}
                >
                  {t("nav.assistance")}
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-300",
                      mobileAssistanceOpen && "rotate-180",
                    )}
                  />
                </button>
                {mobileAssistanceOpen && (
                  <div
                    id="mobile-assistance-menu"
                    className="mb-1 flex flex-col gap-0.5 pl-3"
                    style={{ order: menuRank("support") }}
                  >
                    {ASSISTANCE_ITEMS.map(({ key, label, icon: Icon }) => (
                      <Link
                        key={key}
                        to={localizedPath(key, locale)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted"
                      >
                        <Icon className="h-4 w-4 text-primary" />
                        {t(label)}
                      </Link>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  style={{ order: menuRank("network") }}
                  onClick={() => setMobileEcosystemOpen((value) => !value)}
                  aria-expanded={mobileEcosystemOpen}
                  aria-controls="mobile-ecosystem-menu"
                  className={cn(
                    "flex cursor-pointer items-center justify-between rounded-lg px-3 py-3 text-base font-medium transition-colors",
                    isEcosystemActive
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted",
                  )}
                >
                  {t("nav.ecosystem")}
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-300",
                      mobileEcosystemOpen && "rotate-180",
                    )}
                  />
                </button>
                {mobileEcosystemOpen && (
                  <div
                    id="mobile-ecosystem-menu"
                    className="mb-1 flex flex-col gap-0.5 pl-3"
                    style={{ order: menuRank("network") }}
                  >
                    {ECOSYSTEM_ITEMS.map(({ key, label, icon: Icon }) => (
                      <Link
                        key={key}
                        to={localizedPath(key, locale)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted"
                      >
                        <Icon className="h-4 w-4 text-primary" />
                        {t(label)}
                      </Link>
                    ))}
                  </div>
                )}
                <Button asChild className="order-10 mt-3">
                  <Link
                    to={inquiryPath(locale, { requestType: "new_project" })}
                  >
                    {t("cta.start_project")}
                  </Link>
                </Button>
                <ReservedAreaButton
                  className="order-10 mt-2"
                  fullWidth
                  showRegistration
                  onNavigate={() => setOpen(false)}
                />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
